/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import {
  createLabel,
  ICreateLabelResponse,
  readAllLabels,
} from "../../api/Label";
import { LabelAtom } from "../../atoms/LabelAtom";
import { ILabel } from "../../types/LabelTypes";
import { useModal } from "../Modal/Modal";
import StyledButton from "../StyledButton";
import StyledInput from "../StyledInput";
import Labels from "./Labels";

const ModalBody = styled.div`
  max-width: 394px;
  min-width: 394px;
  max-height: 360px;
  min-height: 240px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  padding: 24px;
  margin: 18px -22px -25px;

  > div + div {
    margin-top: 16px;
  }

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 94px);
    margin: 0;

    justify-content: start;
    grid-gap: 32px;

    border-top: none;
  }
`;

const Buttons = styled.span`
  width: 100%;

  margin-top: 32px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  border-top: 1px solid #eceeee;

  padding: 20px 0 0;

  @media screen and (max-width: 1024px) {
    border-top: 1px solid #cccccc;

    padding: 25px 25px 0;

    position: absolute;
    bottom: 25px;
    left: 0;
    right: 0;

    justify-content: flex-end;
  }
`;

const InputLabel = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > input::placeholder {
    font-size: 16px;
    color: #979797;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f2f2f;

  margin-bottom: 16px;
`;

interface INewLabel {
  name: string;
  color: string;
  labelIndex: number;
}

export default function CreateLabel(): JSX.Element {
  const modalHandler = useModal();
  const [labels, setLabels] = useRecoilState(LabelAtom);
  const [newLabel, setNewLabel] = useState<INewLabel>({
    name: "",
    color: "",
    labelIndex: 0,
  });
  const [labelColorsNotAvailable, setLabelColorsNotAvailable] = useState<
    string[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (labels) setLabelColorsNotAvailable(labels.map((lab) => lab.color));
  }, [labels]);

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    const name = event.target.value;
    setNewLabel((label) => ({
      ...label,
      name,
    }));
  }

  function handleColorChange(color: string) {
    setNewLabel((label) => ({
      ...label,
      color,
    }));
  }

  async function getAllLabels() {
    try {
      const responseAllLabels = await readAllLabels();
      if (responseAllLabels) {
        setLabels(responseAllLabels);
        toast("There is already a label with the same color/name", {
          type: "error",
        });
      } else {
        toast("There was an error fetching labels. Please, try again", {
          type: "error",
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleSaveButton() {
    if (newLabel.name.trim() === "" || !newLabel.color) {
      toast("Name and color are required", { type: "error" });
      return;
    }

    setIsLoading(true);

    let labelIndex =
      (labels &&
        labels.length > 0 &&
        labels.slice().sort((a, b) => b.labelIndex - a.labelIndex)[0]
          .labelIndex) ||
      0;

    if (labelIndex >= 1) {
      labelIndex += 1;
    } else {
      labelIndex = 0;
    }

    try {
      const createdLabel: ICreateLabelResponse = await createLabel({
        ...newLabel,
        labelIndex,
      });

      const formattedCreatedLabel: ILabel = {
        ...createdLabel,
        labelIndex,
      };

      setLabels((prevLabels) => {
        if (prevLabels) {
          return [...prevLabels, formattedCreatedLabel];
        }
        return [formattedCreatedLabel];
      });
    } catch (error) {
      getAllLabels();
    } finally {
      modalHandler.popModal();
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleSaveButton();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [newLabel.name, newLabel.color]);

  return (
    <ModalBody>
      <InputLabel>
        <Title>Label Name</Title>
        <StyledInput
          placeholder="Enter label name"
          onChange={handleNameChange}
        />
      </InputLabel>

      <InputLabel>
        <Title>Label Color</Title>
        <Labels
          labelColorsNotAvailable={labelColorsNotAvailable}
          handleLabelChecked={handleColorChange}
          labelChecked={newLabel.color}
          checkedLabelIndex={0} // Creating a label just pass a random index, that index is to use when It's editing
        />
      </InputLabel>

      <Buttons>
        <StyledButton
          onClick={() => modalHandler.popModal()}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={isLoading}
          onClick={() => !isLoading && handleSaveButton()}
          css={css`
            width: 80px;
          `}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
